import { useMemo } from 'react'
import { Maybe, MediaUploadType } from 'types/graphql'
import { resizedImageUrl } from './imageUrl'
import { ImageResizeOptions as OriginImageResizeOptions, VideoThumbnailResizeOptions } from './imagekit'
import { phoneMaxWidth } from './media'
import { videoThumbnailUrl } from './videoUrl'

type ImageSize = 'tiny' | 'small' | 'medium' | 'large' | 'xLarge'

const DEFAULT_SIZES: ImageSize[] = ['tiny', 'small', 'medium', 'large', 'xLarge']

const SIZES_MULTIPLIER: Record<ImageSize, number> = {
  tiny: 1,
  small: 1.5,
  medium: 2,
  large: 2.5,
  xLarge: 3
}

type ImageResizeOptions = Omit<OriginImageResizeOptions, 'width' | 'height'>
type VideoResizeOptions = Omit<VideoThumbnailResizeOptions, 'width' | 'height'>

interface UseResizedMediaParams {
  mediaUrl?: Maybe<string>
  width: number
  height?: number
  requiredSizes?: ImageSize[]
  type?: MediaUploadType
  imageResizeOptions?: ImageResizeOptions
  videoResizeOptions?: VideoResizeOptions
}

const resizeMedia = ({
  mediaUrl = '',
  width,
  height,
  type = MediaUploadType.Image,
  imageResizeOptions = {},
  videoResizeOptions = {}
}: UseResizedMediaParams) => {
  switch (type) {
    case MediaUploadType.Image:
      return resizedImageUrl(mediaUrl, { width, height, ...imageResizeOptions })
    case MediaUploadType.Video:
      return videoThumbnailUrl(mediaUrl, { width, height, ...videoResizeOptions })
    default:
      throw new Error(`Cannot generate resized media for type: ${type}`)
  }
}

const getResizedMedias = ({ mediaUrl, width, height, requiredSizes, type, imageResizeOptions, videoResizeOptions }: UseResizedMediaParams) => {
  const defaultHeight = height || width

  const areRequiredSizes = requiredSizes && requiredSizes.length > 1
  const sizes = areRequiredSizes ? requiredSizes : DEFAULT_SIZES

  const imageSizes = sizes.reduce((result, size) => {
    const multiplier = SIZES_MULTIPLIER[size]
    const media = resizeMedia({
      mediaUrl,
      width: width * multiplier,
      height: defaultHeight * multiplier,
      type,
      imageResizeOptions,
      videoResizeOptions
    })
    return { ...result, [size]: media }
  }, {} as Record<ImageSize, string>)

  return imageSizes
}

const generateImgAttributes = (images: ReturnType<typeof getResizedMedias>, sizes: typeof SIZES_MULTIPLIER) => {
  let srcSet = ''

  const sizeEntries = Object.entries(sizes) as [ImageSize, number][]
  for (const [size, value] of sizeEntries) {
    if (images.hasOwnProperty(size)) {
      const set = `${images[size]} ${value}w`
      const comma = srcSet ? ',' : ''
      srcSet = `${srcSet} ${comma} ${set}`
    }
  }

  return {
    sizes: `(min-width: ${phoneMaxWidth}px) ${sizes.tiny}px, 100vw`,
    dataSizes: 'auto',
    srcSet,
    dataSrcSet: srcSet
  }
}

const generateFixedImgAttributes = (images: ReturnType<typeof getResizedMedias>, sizes: typeof SIZES_MULTIPLIER) => {
  const imageValues = Object.values(images)

  const srcSet = imageValues
    .reduce((srcSets: string[], image, index) => {
      const density = index === 0 ? '' : `${index + 1}x`
      return [...srcSets, `${image} ${density}`]
    }, [])
    .join(',')

  return {
    sizes: `(min-width: ${phoneMaxWidth}px) ${sizes.tiny}px, 100vw`,
    dataSizes: 'auto',
    srcSet,
    dataSrcSet: srcSet
  }
}

const getLowQualityImage = (params: UseResizedMediaParams) => {
  const { mediaUrl = '', width, height, type } = params

  const DEFAULT_LOW_QUALITY_OPTIONS = {
    quality: 20,
    blur: 1,
    sharpen: 0
  }

  let resizedOptions = {}

  if (params.videoResizeOptions || params.imageResizeOptions) {
    const optionsProperty = type === MediaUploadType.Image ? 'imageResizeOptions' : 'videoResizeOptions'
    resizedOptions = {
      [optionsProperty]: {
        ...params[optionsProperty]
      }
    }
  }

  resizedOptions = {
    ...resizedOptions,
    ...DEFAULT_LOW_QUALITY_OPTIONS
  }

  return resizeMedia({ mediaUrl, width, height: height || width, ...resizedOptions })
}

export const useResizedMedia = (params: UseResizedMediaParams) => {
  const images = useMemo(() => getResizedMedias(params), [params])

  const imageSizes = useMemo(() => {
    return {
      tiny: params.width * SIZES_MULTIPLIER.tiny,
      small: params.width * SIZES_MULTIPLIER.small,
      medium: params.width * SIZES_MULTIPLIER.medium,
      large: params.width * SIZES_MULTIPLIER.large,
      xLarge: params.width * SIZES_MULTIPLIER.xLarge
    }
  }, [params])

  const imgAttributes = useMemo(() => {
    const isFixed = params.requiredSizes && params.requiredSizes.length === 1

    if (isFixed) {
      return generateFixedImgAttributes(images, imageSizes)
    }
    return generateImgAttributes(images, imageSizes)
  }, [images, imageSizes, params.requiredSizes])

  const lazyLowQualityImage = useMemo(() => getLowQualityImage(params), [params])

  return {
    lazyLowQualityImage,
    ...images,
    imgAttributes,
    imageSizes
  }
}
